import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import { message } from "antd";
import SettingsLayout from "../components/SettingsLayout";
import CustomButton from "components/Common/ui/CustomButton";
import { useMessagingSettings } from "../hooks/useMessagingSettings";
import {
  MessagingRole,
  MessagingConfig,
} from "../../../types/instanceTypes";
import CustomTooltip from "components/Common/ui/CustomTooltip";

// Backend scope mapping
const SCOPE_MAP = {
  student: {
    student: 'classmates',          
    teacher: 'class_teachers',      
    guardian: 'by_role',            
    director: 'by_role',            
    manager: 'by_role',             
    user: 'by_role',                
  },
  teacher: {
    student: 'my_students',             
    guardian: 'my_students_guardians', 
    teacher: 'by_role',                 
    director: 'by_role',
    manager: 'by_role',
    user: 'by_role',                    
  },
  guardian: {
    student: 'by_role',                 
    teacher: 'children_teachers',       
    guardian: 'by_role',                
    director: 'by_role',
    manager: 'by_role',
    user: 'by_role',                    
  },
  director: {
    student: 'by_role',                 
    teacher: 'by_role',                 
    guardian: 'by_role',                
    director: 'by_role',                
    manager: 'by_role',
    user: 'by_role',
  },
  manager: {
    student: 'by_role',
    teacher: 'by_role',
    guardian: 'by_role',
    director: 'by_role',
    manager: 'by_role',
    user: 'by_role',
  },
  user: {
    student: 'by_role',
    teacher: 'by_role',
    guardian: 'by_role',
    director: 'by_role',
    manager: 'by_role',
    user: 'by_role',
  },
};

const MessagingSettings: React.FC = () => {
  const { t } = useTranslation();

  const {
    messagingConfig,
    setMessagingConfig,
    loading,
    error,
    saveMessagingSettings,
    fetchMessagingSettings
  } = useMessagingSettings();

  const roles: { id: MessagingRole; label: string }[] = [
    { id: "student", label: t("messagingSettings.roles.student") },
    { id: "guardian", label: t("messagingSettings.roles.guardian") },
    { id: "teacher", label: t("messagingSettings.roles.teacher") },
    { id: "director", label: t("messagingSettings.roles.director") },
    { id: "manager", label: t("messagingSettings.roles.manager") },
    { id: "user", label: t("messagingSettings.roles.user") },
  ];

  const allowedTargetsPerRole: Record<MessagingRole, MessagingRole[]> = {
    student: ["student", "teacher", "director", "manager"],
    teacher: ["student", "guardian", "director", "manager"],
    guardian: ["teacher", "director", "manager"],
    director: ["student", "teacher", "guardian", "manager", "user"],
    manager: ["student", "teacher", "guardian", "director", "manager", "user"],
    user: ["director", "manager"],
  };

  useEffect(() => {
    fetchMessagingSettings()
  }, []);

  // Ensure config always contains all roles
  useEffect(() => {
    if (!messagingConfig) return
    setMessagingConfig((prev: MessagingConfig | null) => {
      const current = prev || {};
      const updated: MessagingConfig = { ...current };

      roles.forEach(({ id }) => {
        if (!updated[id]) {
          updated[id] = { canMessage: [] };
        } else if (!updated[id]!.canMessage) {
          updated[id]!.canMessage = [];
        }
      });

      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (
    senderRole: MessagingRole,
    targetRole: MessagingRole,
    checked: boolean
  ) => {
    setMessagingConfig((prev: MessagingConfig | null) => {
      const current = prev || {};
      const senderConfig = current[senderRole] || { canMessage: [] };
      let canMessage = [...(senderConfig.canMessage || [])];

      if (checked) {
        if (!canMessage.includes(targetRole)) {
          canMessage.push(targetRole);
        }
      } else {
        canMessage = canMessage.filter((r) => r !== targetRole);
      }

      return {
        ...current,
        [senderRole]: {
          ...senderConfig,
          canMessage,
        },
      };
    });
  };

  const handleSubmit = async () => {
    if (!messagingConfig) return;
    await saveMessagingSettings({
      messagingConfig,
    });
  };

  const getRoleLabel = (roleId: MessagingRole) => {
    const role = roles.find((r) => r.id === roleId);
    return role ? role.label : roleId;
  };

  // Get description key from SCOPE_MAP
  const getDescriptionKey = (senderRole: MessagingRole, targetRole: MessagingRole): string => {
    try {
      const scope = SCOPE_MAP[senderRole]?.[targetRole];
      return scope ? `messagingSettings.scopes.${scope}` : 'messagingSettings.scopes.by_role';
    } catch (error) {
      return 'messagingSettings.scopes.by_role';
    }
  };

  return (
    <SettingsLayout
      titleKey="messagingSettings.pageTitle"
      loading={loading}
    >
      <Form>
        <div className="mb-4">
          <h5 className="mb-3">
            {t("messagingSettings.title")}
          </h5>

          <p className="text-muted mb-4">
            {t("messagingSettings.description")}
          </p>

          {roles.map((sender) => {
            const allowedTargets =
              allowedTargetsPerRole[sender.id] || [];

            const senderConfig = messagingConfig?.[sender.id] || {
              canMessage: [],
            };

            return (
              <div key={sender.id} className="card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    {getRoleLabel(sender.id)}
                  </h6>

                  <span className="text-muted small">
                    {t("messagingSettings.senderCanMessage")}
                  </span>
                </div>

                <div className="row">
                  {allowedTargets.map((targetRole) => (
                    <div key={targetRole} className="col-md-4 mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          id={`${sender.id}-${targetRole}`}
                          label={getRoleLabel(targetRole)}
                          checked={senderConfig.canMessage.includes(targetRole)}
                          onChange={(e) =>
                            handleToggle(
                              sender.id,
                              targetRole,
                              e.target.checked
                            )
                          }
                        />
                        <CustomTooltip
                          content={t(getDescriptionKey(sender.id, targetRole))}
                          placement="top"
                          maxWidth={280}
                          className="ms-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-end pb-4">
          <CustomButton
            variant="primary"
            onClick={handleSubmit}
            className="px-5"
          >
            <i className="fas fa-save me-2" />
            {t("messagingSettings.saveChanges")}
          </CustomButton>
        </div>
      </Form>
    </SettingsLayout>
  );
};

export default MessagingSettings;